import React from 'react';
import { classes } from 'typestyle';
import { getLargeDate } from '../../../utils';
import Button from '../../layout/button/button.component';
import Select from '../../layout/form/select.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import Icon from '../../layout/icon/icon.component';
import {
    resutsButtonpadSelectClassName,
    resutsButtonpadButtonClassName,
    resutsButtonpadIconClassName,
    resutsButtonpadIconDropdownClassName
} from './results-buttonpad.style';
import { buttonClassName, buttonLightClassName } from '../../layout/button/button.style';
import { downloadPDFTable } from '../../../services/pdf.service';
import { linkCSVTable } from '../../../services/csv.service';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { Option } from '../../../types';
import { DATETIME_1_JAN_2022, LABEL_FOR_CURRENT_DATE } from '../../../constants';

type TableData = {
    title: string,
    subtitle?: string,
    headers: string[],
    rows: RowData[],
};

type RowData = CellData[] | undefined | false | null;
type CellData = string | number;

interface ResultsButtonpadProps {
    downloadData?: {
        fileName: string,
        tables?: TableData[] | (() => TableData[]),
        fileTitle?: string,
        headers?: string[],
        content?: ((string | number)[])[],
    },
    pdf?: boolean | string | PDFButtonOptions,
    csv?: boolean | string,
    datetime?: number | null,
    localLevel?: boolean,
    onChangeDate?: (newDatetime: number | null) => void,
    onChangeLocalLevel?: (newLocalLevel: boolean) => void
}

type PDFButtonOptions = {
    columnSizePercentages?: number[],
    buttonText?: string
}

const ResultsButtonpad: React.FC<ResultsButtonpadProps> = ({
    downloadData,
    pdf, csv,
    datetime, onChangeDate,
    localLevel, onChangeLocalLevel
}) => {
    const [{taxonomies: {dates}}] = useAppState();
    let tables: TableData[] = [];

    if (downloadData && downloadData.tables) {
        tables = typeof downloadData.tables === 'function'
            ? downloadData.tables()
            : downloadData.tables;
    }

    const dateValue = (!datetime || (datetime === null))
        ? LABEL_FOR_CURRENT_DATE
        : String(datetime);

    const dateLabel = (!datetime || (datetime === null))
        ? LABEL_FOR_CURRENT_DATE
        : getLargeDate(datetime);
    const localLevelValue = (typeof localLevel === 'boolean')
        ? localLevel ? 'yes' : 'no'
        : 'none';
    const localLevelLabel = (localLevelValue === 'no')
        ? 'Parliament'
        : (localLevelValue === 'yes')
            ? 'Local government'
            : null;

    const isValidRow = (rowData: RowData): boolean => (
        Array.isArray(rowData) && rowData.length > 0
    );

    const handleChangeDate = (selectedValue: string) => {
        onChangeDate && onChangeDate(
            selectedValue === LABEL_FOR_CURRENT_DATE
            ? null
            : Number(selectedValue)
        );
    };

    const handleChangeLocalLevel = (yesOrNo: string) => {
        onChangeLocalLevel && onChangeLocalLevel(
            yesOrNo === 'yes'
                ? true
                : false
        );
    }

    const askCSVDownload = (csv === true) || (typeof csv === 'string');

    const csvLinkData = (askCSVDownload && downloadData) ? linkCSVTable(
        tables.map(tableData => ({
            title: tableData.title + ' - ' + (tableData.subtitle),
            headers: tableData.headers,
            content: tableData.rows.filter(row => isValidRow(row)) as (string | number)[][],
            dataDate: dateLabel
        }))
    ) : null;

    const pdfDownloadHandler = (pdf && downloadData) ? () => {
        downloadPDFTable(
            downloadData.fileName,
            dateLabel,
            tables.map(tableData => ({
                title: tableData.title,
                subtitle: tableData.subtitle,
                headers: tableData.headers,
                rows: tableData.rows.filter(row => isValidRow(row)) as (string | number)[][],
                columnSizePercentages: (typeof pdf !== 'boolean') && (typeof pdf !== 'string')
                    ? pdf.columnSizePercentages
                    : undefined
            }))
        );
    } : null;

    const getDateOptions = (): Option[] => [
        {
            value: LABEL_FOR_CURRENT_DATE,
            label: LABEL_FOR_CURRENT_DATE
        },
        ...dates
            .filter(taxonomyDate => localLevel ? taxonomyDate.datetime !== DATETIME_1_JAN_2022 : true)
            .map(taxonomyDate => ({
                value: String(taxonomyDate.datetime),
                label: getLargeDate(taxonomyDate.datetime)
            }))
    ];

    const downloadPDFButtonText = typeof pdf === 'string'
        ? pdf
        : (typeof pdf === 'object') && (pdf.buttonText !== undefined)
            ? pdf.buttonText
            : 'Download .pdf';

    const downloadCSVButtonText = typeof csv === 'string'
        ? csv
        : 'Download .csv';

    return (
        <Grid align='center' alignTablet='right'>
            <Cell size={ 'stack' }>
                { (localLevel !== undefined) && (onChangeLocalLevel) && (
                    <Select
                        options={ [{ value: 'no', label: 'Parliament' }, { value: 'yes', label: 'Local government' }] }
                        value={ localLevelValue }
                        onChange={ handleChangeLocalLevel }
                    >
                        <div className={ resutsButtonpadSelectClassName }>
                            { localLevelLabel }
                            <Icon
                                name='dropdown'
                                className={ classes(
                                    resutsButtonpadIconClassName,
                                    resutsButtonpadIconDropdownClassName
                                ) }/>
                        </div>
                    </Select>
                ) }
            </Cell>
            <Cell size={ 'stack' }>
                { dateValue && dateLabel && (
                    <Select
                        options={ getDateOptions() }
                        value={ dateValue }
                        onChange={ handleChangeDate }
                    >
                        <div className={ resutsButtonpadSelectClassName }>
                            { dateLabel }
                            <Icon
                                name='dropdown'
                                className={ classes(
                                    resutsButtonpadIconClassName,
                                    resutsButtonpadIconDropdownClassName
                                ) }/>
                        </div>
                    </Select>
                ) }
            </Cell>
            { pdfDownloadHandler && (
                <Cell size={ 'stack' }>
                    <Button format='light' onClick={ pdfDownloadHandler } className={ resutsButtonpadButtonClassName }>
                        { downloadPDFButtonText } <Icon name='download' className={ resutsButtonpadIconClassName }/>
                    </Button>
                </Cell>
            ) }
            { (csvLinkData && downloadData) && (
                <Cell size={ 'stack' }>
                    <a
                        href={ csvLinkData }
                        target='_blank'
                        rel='noreferrer'
                        download={ downloadData.fileName + '.csv' }
                        className={ classes(buttonClassName, buttonLightClassName, resutsButtonpadButtonClassName) }>
                        { downloadCSVButtonText } <Icon name='download' className={ resutsButtonpadIconClassName }/>
                    </a>
                </Cell>
            ) }
        </Grid>
    );
};

export default ResultsButtonpad;
